import {
  Box,
  Collapse,
  Container,
  Group,
  Menu,
  Stack,
  Text,
  Tooltip,
  UnstyledButton
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { PriceList, PriceListItem } from '@/api/generated/models';
import type { InfoField } from '@/components/common';
import {
  getGetPriceListQueryKey,
  getListItemsQueryKey,
  getListPriceListsQueryKey,
  getSearchPriceListsQueryKey,
  useActivateItem,
  useActivatePriceList,
  useDeactivateItem,
  useDeactivatePriceList,
  useGetPriceList,
  useListItems
} from '@/api/generated/price-lists/price-lists';
import { useListUnits } from '@/api/generated/units/units';
import { IconFigmaChevronDown } from '@/assets/icons';
import {
  BackButton,
  ContentCard,
  InfoCard,
  KebabTrigger,
  ListPagination,
  SearchField,
  SelectField,
  StatusBanner,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_COL_GAP,
  tableCenteredCellStyle,
  Toast
} from '@/components/common';
import { DeactivatePriceListItemModal } from '@/features/price-lists/components/DeactivatePriceListItemModal';
import { DeactivatePriceListModal } from '@/features/price-lists/components/DeactivatePriceListModal';
import { PriceListFormModal } from '@/features/price-lists/components/PriceListFormModal';
import { PriceListItemFormModal } from '@/features/price-lists/components/PriceListItemFormModal';
import { findUnitLabel } from '@/features/units/unitDisplay';
import { formatAmount, formatDate, formatNumber } from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { useLocalPagination } from '@/utils/useLocalPagination';

const typeLabelKey = (plType?: string) => {
  switch (plType) {
    case 'CONTRACT':
      return 'priceLists.typeContract';
    case 'SPECIAL':
      return 'priceLists.typeSpecial';
    default:
      return 'priceLists.typeBase';
  }
};

const DESCRIPTION_TRUNCATE_LENGTH = 160;

export const PriceListDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: unitsData } = useListUnits();

  // refetchOnMount: 'always' — ne smije prikazati zastarjeli status/stavke
  // iz keša kad se otvori detalj s liste.
  const { data } = useGetPriceList(id ?? '', {
    query: { enabled: !!id, refetchOnMount: 'always' }
  });
  const priceList = data as PriceList | undefined;
  const { data: items = [] } = useListItems(id ?? '', {
    query: { enabled: !!id, refetchOnMount: 'always' }
  });

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [mobileDescriptionOpened, setMobileDescriptionOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const [editModalOpened, setEditModalOpened] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [deactivateModalOpened, setDeactivateModalOpened] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const handlePreview = async () => {
    if (!id) return;
    setPreviewError(false);
    setIsPreviewLoading(true);
    try {
      await openBinaryInNewTab(`/api/v1/price-lists/${id}/pdf`);
    } catch {
      setPreviewError(true);
    } finally {
      setIsPreviewLoading(false);
    }
  };
  const [itemModalState, setItemModalState] = useState<{ open: boolean; item?: PriceListItem }>({
    open: false
  });
  const [deactivateItem, setDeactivateItem] = useState<PriceListItem | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const categories = useMemo(
    () => Array.from(new Set(items.map((item) => item.category).filter(Boolean))) as string[],
    [items]
  );

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const matchesCategory = !categoryFilter || item.category === categoryFilter;
        const q = search.trim().toLowerCase();
        const matchesSearch =
          !q ||
          item.itemCode?.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q) ||
          item.sortOrder?.toString().includes(q);
        return matchesCategory && matchesSearch;
      }),
    [items, search, categoryFilter]
  );

  const activeItemCount = items.filter((item) => item.isActive).length;

  const {
    page: itemsPage,
    setPage: setItemsPage,
    pageSize: itemsPageSize,
    setPageSize: setItemsPageSize,
    totalPages: itemsTotalPages,
    pageItems: pagedItems
  } = useLocalPagination(filteredItems);

  const description = priceList?.description ?? '';
  const isLongDescription = description.length > DESCRIPTION_TRUNCATE_LENGTH;
  const displayedDescription =
    isLongDescription && !descriptionExpanded
      ? `${description.slice(0, DESCRIPTION_TRUNCATE_LENGTH)}…`
      : description;

  const deactivatePriceListMutation = useDeactivatePriceList({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetPriceListQueryKey(id ?? '') });
        queryClient.invalidateQueries({ queryKey: getListPriceListsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchPriceListsQueryKey() });
        setSavedMessage(t('priceLists.deactivateSuccess'));
        setDeactivateModalOpened(false);
      }
    }
  });

  const deactivateItemMutation = useDeactivateItem({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListItemsQueryKey(id ?? '') });
        setSavedMessage(t('priceLists.itemDeactivateSuccess'));
        setDeactivateItem(null);
      }
    }
  });

  const activatePriceListMutation = useActivatePriceList({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetPriceListQueryKey(id ?? '') });
        queryClient.invalidateQueries({ queryKey: getListPriceListsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchPriceListsQueryKey() });
        setSavedMessage(t('priceLists.activateSuccess'));
      }
    }
  });

  const activateItemMutation = useActivateItem({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListItemsQueryKey(id ?? '') });
        setSavedMessage(t('priceLists.itemActivateSuccess'));
      }
    }
  });

  if (!priceList) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('priceLists.detailNotAvailable')}
        </Text>
      </Container>
    );
  }

  const infoFields: InfoField[] = [
    { label: t('priceLists.colType'), value: t(typeLabelKey(priceList.plType)) },
    { label: t('priceLists.fieldValidFrom'), value: formatDate(priceList.validFrom) },
    {
      label: t('priceLists.fieldValidTo'),
      value: priceList.validTo ? formatDate(priceList.validTo) : t('priceLists.noEndDate')
    },
    { label: t('priceLists.colItemsCount'), value: String(activeItemCount) }
  ];

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/templates/price-lists" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {previewError && <Toast tone="danger">{t('priceLists.previewError')}</Toast>}

        <Stack gap={12} hiddenFrom="sm">
          <Text fz={22} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('priceLists.detailTitlePrefix')}
            {priceList.name}
          </Text>
          <Group gap={12} wrap="nowrap">
            <UnstyledButton
              onClick={() => setEditModalOpened(true)}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px 20px',
                borderRadius: 6,
                border: '1px solid var(--color-border-default)',
                backgroundColor: 'var(--color-bg-surface)',
                color: 'var(--color-text-primary)',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {t('priceLists.editPriceList')}
            </UnstyledButton>
            {priceList.isActive ? (
              <UnstyledButton
                onClick={() => setDeactivateModalOpened(true)}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '12px 20px',
                  borderRadius: 6,
                  border: '1px solid var(--color-status-danger)',
                  backgroundColor: 'var(--color-bg-surface)',
                  color: 'var(--color-status-danger)',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                {t('priceLists.deactivateTitle')}
              </UnstyledButton>
            ) : (
              <UnstyledButton
                onClick={() => id && activatePriceListMutation.mutate({ id })}
                disabled={activatePriceListMutation.isPending}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '12px 20px',
                  borderRadius: 6,
                  backgroundColor: 'var(--color-accent-primary)',
                  color: 'var(--color-text-on-accent)',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: activatePriceListMutation.isPending ? 0.6 : 1
                }}
              >
                {t('priceLists.activateTitle')}
              </UnstyledButton>
            )}
          </Group>
          <UnstyledButton
            onClick={handlePreview}
            disabled={isPreviewLoading}
            style={{
              width: '100%',
              textAlign: 'center',
              padding: '12px 20px',
              borderRadius: 6,
              border: '1px solid var(--color-accent-primary)',
              backgroundColor: 'var(--color-accent-primary)',
              color: 'var(--color-text-on-accent)',
              fontSize: 14,
              fontWeight: 600,
              opacity: isPreviewLoading ? 0.6 : 1
            }}
          >
            {t('priceLists.previewButton')}
          </UnstyledButton>

          <UnstyledButton
            onClick={() => setMobileDescriptionOpened((v) => !v)}
            style={{
              width: '100%',
              padding: '16px 20px',
              borderRadius: 12,
              border: '1px solid var(--color-border-soft)',
              backgroundColor: 'var(--color-bg-surface)'
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {t('priceLists.descriptionTitle')}
              </Text>
              <div
                style={{
                  display: 'flex',
                  transform: mobileDescriptionOpened ? 'rotate(180deg)' : undefined,
                  transition: 'transform 150ms ease'
                }}
              >
                <IconFigmaChevronDown size={16} color="var(--color-text-secondary)" />
              </div>
            </Group>
            <Collapse in={mobileDescriptionOpened}>
              <Text fz={13.5} ta="left" pt={12} style={{ color: 'var(--color-text-secondary)' }}>
                {priceList.description || t('priceLists.detailNotAvailable')}
              </Text>
            </Collapse>
          </UnstyledButton>

          <Stack
            gap={14}
            p={16}
            style={{
              borderRadius: 12,
              border: '1px solid var(--color-border-soft)',
              backgroundColor: 'var(--color-bg-surface)'
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {t('priceLists.colType')}
              </Text>
              <StatusPill
                label={t(typeLabelKey(priceList.plType))}
                bg="var(--color-role-user-soft)"
                fg="var(--color-role-user)"
              />
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {t('priceLists.colStatus')}
              </Text>
              {priceList.isActive ? (
                <StatusPill
                  label={t('priceLists.statusActive')}
                  bg="var(--color-status-success-soft)"
                  fg="var(--color-status-success)"
                />
              ) : (
                <StatusPill
                  label={t('priceLists.statusInactive')}
                  bg="var(--color-status-draft-soft)"
                  fg="var(--color-status-draft)"
                />
              )}
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {t('priceLists.fieldValidFrom')}
              </Text>
              <Text fz={13.5} style={{ color: 'var(--color-text-primary)' }}>
                {formatDate(priceList.validFrom)}
              </Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {t('priceLists.fieldValidTo')}
              </Text>
              <Text fz={13.5} style={{ color: 'var(--color-text-primary)' }}>
                {priceList.validTo ? formatDate(priceList.validTo) : t('priceLists.noEndDate')}
              </Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {t('priceLists.colItemsCount')}
              </Text>
              <Text fz={13.5} style={{ color: 'var(--color-text-primary)' }}>
                {activeItemCount}
              </Text>
            </Group>
          </Stack>
        </Stack>

        <Group justify="space-between" wrap="nowrap" visibleFrom="sm">
          <Group gap={12} wrap="nowrap">
            <Text fz={24} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {t('priceLists.detailTitlePrefix')}
              {priceList.name}
            </Text>
            {priceList.isActive ? (
              <StatusPill
                header
                label={t('priceLists.statusActive')}
                bg="var(--color-status-success-soft)"
                fg="var(--color-status-success)"
              />
            ) : (
              <StatusPill
                header
                label={t('priceLists.statusInactive')}
                bg="var(--color-status-draft-soft)"
                fg="var(--color-status-draft)"
              />
            )}
          </Group>
          <Group gap={12} wrap="nowrap">
            <UnstyledButton
              onClick={handlePreview}
              disabled={isPreviewLoading}
              style={{
                padding: '12px 20px',
                borderRadius: 6,
                border: '1px solid var(--color-accent-primary)',
                backgroundColor: 'var(--color-accent-primary)',
                color: 'var(--color-text-on-accent)',
                fontSize: 14,
                fontWeight: 600,
                opacity: isPreviewLoading ? 0.6 : 1
              }}
            >
              {t('priceLists.previewButton')}
            </UnstyledButton>
            {priceList.isActive ? (
              <UnstyledButton
                onClick={() => setDeactivateModalOpened(true)}
                style={{
                  padding: '12px 20px',
                  borderRadius: 6,
                  border: '1px solid var(--color-status-danger)',
                  backgroundColor: 'var(--color-bg-surface)',
                  color: 'var(--color-status-danger)',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                {t('priceLists.deactivateTitle')}
              </UnstyledButton>
            ) : (
              <UnstyledButton
                onClick={() => id && activatePriceListMutation.mutate({ id })}
                disabled={activatePriceListMutation.isPending}
                style={{
                  padding: '12px 20px',
                  borderRadius: 6,
                  backgroundColor: 'var(--color-accent-primary)',
                  color: 'var(--color-text-on-accent)',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: activatePriceListMutation.isPending ? 0.6 : 1
                }}
              >
                {t('priceLists.activateTitle')}
              </UnstyledButton>
            )}
            <UnstyledButton
              onClick={() => setEditModalOpened(true)}
              style={{
                padding: '12px 20px',
                borderRadius: 6,
                border: '1px solid var(--color-border-default)',
                backgroundColor: 'var(--color-bg-surface)',
                color: 'var(--color-text-primary)',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {t('priceLists.editPriceList')}
            </UnstyledButton>
          </Group>
        </Group>

        <Box visibleFrom="sm" w="100%">
          <InfoCard
            title={t('priceLists.basicInfoTitle')}
            fields={infoFields}
            columns={4}
            borderColor="var(--color-border-default)"
            trailing={
              <Stack gap={4}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('priceLists.descriptionTitle')}
                </Text>
                <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
                  {displayedDescription || t('priceLists.detailNotAvailable')}
                </Text>
                {isLongDescription && (
                  <UnstyledButton
                    onClick={() => setDescriptionExpanded((v) => !v)}
                    style={{
                      color: 'var(--color-accent-primary-active)',
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  >
                    {t(descriptionExpanded ? 'priceLists.lessLabel' : 'priceLists.moreLabel')}
                  </UnstyledButton>
                )}
              </Stack>
            }
          />
        </Box>

        <Box visibleFrom="sm" w="100%">
          <ContentCard>
            <Stack gap={16} p={20}>
              <Group justify="space-between" wrap="nowrap">
                <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {t('priceLists.itemsTableTitle')}
                </Text>
                <Group gap={8} wrap="nowrap">
                  <Tooltip label={t('priceLists.importFromExcelComingSoon')}>
                    <UnstyledButton
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: '1px solid var(--color-border-default)',
                        backgroundColor: 'var(--color-bg-surface)',
                        color: 'var(--color-text-muted)',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'not-allowed'
                      }}
                    >
                      {t('priceLists.importFromExcel')}
                    </UnstyledButton>
                  </Tooltip>
                  <UnstyledButton
                    onClick={() => setItemModalState({ open: true })}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      backgroundColor: 'var(--color-accent-primary)',
                      color: 'var(--color-text-on-accent)',
                      fontSize: 13,
                      fontWeight: 600
                    }}
                  >
                    + {t('priceLists.addItem')}
                  </UnstyledButton>
                </Group>
              </Group>

              <Group gap={12} wrap="wrap">
                <div style={{ flex: 1, minWidth: 220 }}>
                  <SearchField
                    hideLabel
                    placeholder={t('priceLists.itemSearchPlaceholder')}
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                </div>
                <div style={{ width: 200 }}>
                  <SelectField
                    hideLabel
                    placeholder={t('priceLists.allCategories')}
                    clearable
                    data={[
                      { value: '', label: t('priceLists.allCategories') },
                      ...categories.map((c) => ({ value: c, label: c }))
                    ]}
                    value={categoryFilter ?? ''}
                    onChange={(value) => setCategoryFilter(value || null)}
                  />
                </div>
              </Group>

              {filteredItems.length === 0 ? (
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('common.noData')}
                </Text>
              ) : (
                <Stack gap={0}>
                  <Group
                    className="table-header-row"
                    gap={TABLE_COL_GAP}
                    wrap="nowrap"
                    py={4}
                    style={{ borderBottom: '1px solid var(--color-border-soft)' }}
                  >
                    <Text fz={11} fw={600} w={40} style={{ color: 'var(--color-text-muted)' }}>
                      {t('priceLists.fieldSortOrder')}
                    </Text>
                    <Text fz={11} fw={600} w={90} style={{ color: 'var(--color-text-muted)' }}>
                      {t('priceLists.fieldItemCode')}
                    </Text>
                    <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
                      {t('priceLists.colItemName')}
                    </Text>
                    <Text fz={11} fw={600} w={150} style={{ color: 'var(--color-text-muted)' }}>
                      {t('priceLists.colCategory')}
                    </Text>
                    <Text fz={11} fw={600} w={90} style={{ color: 'var(--color-text-muted)' }}>
                      {t('priceLists.colItemUnit')}
                    </Text>
                    <Text
                      fz={11}
                      fw={600}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {t('priceLists.colItemUnitPrice')}
                    </Text>
                    <Text
                      fz={11}
                      fw={600}
                      w={90}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {t('priceLists.colItemNormHours')}
                    </Text>
                    <Text
                      fz={11}
                      fw={600}
                      w={TABLE_ACTION_COL_WIDTH}
                      style={{ ...tableCenteredCellStyle, color: 'var(--color-text-muted)' }}
                    >
                      {t('priceLists.colAction')}
                    </Text>
                  </Group>

                  {pagedItems.map((item, index) => (
                    <Group
                      key={item.id}
                      gap={TABLE_COL_GAP}
                      wrap="nowrap"
                      py={8}
                      style={{
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text fz={13} w={40} style={{ color: 'var(--color-text-muted)' }}>
                        {item.sortOrder ?? '—'}
                      </Text>
                      <Text fz={13} w={90} style={{ color: 'var(--color-text-muted)' }}>
                        {item.itemCode}
                      </Text>
                      <Group gap={6} style={{ flex: 1 }} wrap="nowrap">
                        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                          {item.description}
                        </Text>
                        {!item.isActive && (
                          <StatusPill
                            label={t('priceLists.itemStatusInactive')}
                            bg="var(--color-status-draft-soft)"
                            fg="var(--color-status-draft)"
                          />
                        )}
                      </Group>
                      <Text
                        fz={13}
                        w={150}
                        truncate="end"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {item.category}
                      </Text>
                      <Text fz={13} w={90} style={{ color: 'var(--color-text-muted)' }}>
                        {findUnitLabel(unitsData, item.unitId, i18n.language)}
                      </Text>
                      <Text fz={13} w={100} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                        {formatAmount(item.unitPrice)}
                      </Text>
                      <Text fz={13} w={90} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                        {formatNumber(item.normHours)}
                      </Text>
                      <Group w={TABLE_ACTION_COL_WIDTH} justify="center" wrap="nowrap">
                        <Menu
                          opened={menuOpenId === item.id}
                          onChange={(opened) => setMenuOpenId(opened ? (item.id ?? null) : null)}
                          position="bottom-end"
                          shadow="md"
                          radius="md"
                          width={200}
                        >
                          <Menu.Target>
                            <KebabTrigger />
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              styles={{ item: { color: 'var(--color-text-primary)' } }}
                              onClick={() => setItemModalState({ open: true, item })}
                            >
                              {t('priceLists.detailsLink')}
                            </Menu.Item>
                            {item.isActive ? (
                              <Menu.Item
                                styles={{ item: { color: 'var(--color-status-danger)' } }}
                                onClick={() => setDeactivateItem(item)}
                              >
                                {t('priceLists.deactivateConfirm')}
                              </Menu.Item>
                            ) : (
                              <Menu.Item
                                styles={{ item: { color: 'var(--color-status-success)' } }}
                                onClick={() =>
                                  item.id &&
                                  activateItemMutation.mutate({ id: id ?? '', itemId: item.id })
                                }
                              >
                                {t('priceLists.activateConfirm')}
                              </Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              )}

              {filteredItems.length > 0 && (
                <ListPagination
                  label={t('priceLists.itemsShowing', {
                    shown: pagedItems.length,
                    total: filteredItems.length
                  })}
                  page={itemsPage}
                  totalPages={itemsTotalPages}
                  onPageChange={setItemsPage}
                  pageSize={itemsPageSize}
                  onPageSizeChange={setItemsPageSize}
                />
              )}
            </Stack>
          </ContentCard>
        </Box>

        <Stack gap={12} hiddenFrom="sm" w="100%">
          <Text fz={17} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('priceLists.itemsTableTitle')}
          </Text>
          <UnstyledButton
            onClick={() => setItemModalState({ open: true })}
            style={{
              width: '100%',
              textAlign: 'center',
              padding: '12px 20px',
              borderRadius: 6,
              backgroundColor: 'var(--color-accent-primary)',
              color: 'var(--color-text-on-accent)',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            + {t('priceLists.addItem')}
          </UnstyledButton>

          <SearchField
            hideLabel
            placeholder={t('priceLists.itemSearchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <SelectField
            hideLabel
            placeholder={t('priceLists.allCategories')}
            clearable
            data={[
              { value: '', label: t('priceLists.allCategories') },
              ...categories.map((c) => ({ value: c, label: c }))
            ]}
            value={categoryFilter ?? ''}
            onChange={(value) => setCategoryFilter(value || null)}
          />

          {filteredItems.length === 0 ? (
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('common.noData')}
            </Text>
          ) : (
            <Stack gap={12}>
              {pagedItems.map((item) => (
                <Stack
                  key={item.id}
                  gap={10}
                  p={16}
                  style={{
                    borderRadius: 12,
                    border: '1px solid var(--color-border-soft)',
                    backgroundColor: 'var(--color-bg-surface)'
                  }}
                >
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Group gap={6} wrap="wrap">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {item.itemCode}
                        </Text>
                        {!item.isActive && (
                          <StatusPill
                            label={t('priceLists.itemStatusInactive')}
                            bg="var(--color-status-draft-soft)"
                            fg="var(--color-status-draft)"
                          />
                        )}
                      </Group>
                      <Text fz={14} style={{ color: 'var(--color-text-primary)' }}>
                        {item.description}
                      </Text>
                    </Stack>
                    <Menu
                      opened={menuOpenId === item.id}
                      onChange={(opened) => setMenuOpenId(opened ? (item.id ?? null) : null)}
                      position="bottom-end"
                      shadow="md"
                      radius="md"
                      width={200}
                    >
                      <Menu.Target>
                        <KebabTrigger />
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          styles={{ item: { color: 'var(--color-text-primary)' } }}
                          onClick={() => setItemModalState({ open: true, item })}
                        >
                          {t('priceLists.detailsLink')}
                        </Menu.Item>
                        {item.isActive ? (
                          <Menu.Item
                            styles={{ item: { color: 'var(--color-status-danger)' } }}
                            onClick={() => setDeactivateItem(item)}
                          >
                            {t('priceLists.deactivateConfirm')}
                          </Menu.Item>
                        ) : (
                          <Menu.Item
                            styles={{ item: { color: 'var(--color-status-success)' } }}
                            onClick={() =>
                              item.id &&
                              activateItemMutation.mutate({ id: id ?? '', itemId: item.id })
                            }
                          >
                            {t('priceLists.activateConfirm')}
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <Group gap={16} wrap="nowrap">
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Text
                        fz={10}
                        fw={500}
                        tt="uppercase"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {t('priceLists.colCategory')}
                      </Text>
                      <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                        {item.category}
                      </Text>
                    </Stack>
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Text
                        fz={10}
                        fw={500}
                        tt="uppercase"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {t('priceLists.colItemUnit')}
                      </Text>
                      <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                        {findUnitLabel(unitsData, item.unitId, i18n.language)}
                      </Text>
                    </Stack>
                  </Group>
                  <Group gap={16} wrap="nowrap">
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Text
                        fz={10}
                        fw={500}
                        tt="uppercase"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {t('priceLists.colItemUnitPrice')}
                      </Text>
                      <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                        {formatAmount(item.unitPrice)}
                      </Text>
                    </Stack>
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Text
                        fz={10}
                        fw={500}
                        tt="uppercase"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {t('priceLists.colItemNormHours')}
                      </Text>
                      <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                        {formatNumber(item.normHours)}
                      </Text>
                    </Stack>
                  </Group>
                </Stack>
              ))}
            </Stack>
          )}

          {filteredItems.length > 0 && (
            <ListPagination
              label={t('priceLists.itemsShowing', {
                shown: pagedItems.length,
                total: filteredItems.length
              })}
              page={itemsPage}
              totalPages={itemsTotalPages}
              onPageChange={setItemsPage}
              pageSize={itemsPageSize}
              onPageSizeChange={setItemsPageSize}
              bordered={false}
            />
          )}
        </Stack>
      </Stack>

      <PriceListFormModal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        priceList={priceList}
        onSaved={setSavedMessage}
      />

      <PriceListItemFormModal
        opened={itemModalState.open}
        onClose={() => setItemModalState({ open: false })}
        priceListId={id ?? ''}
        priceListName={priceList.name ?? ''}
        item={itemModalState.item}
        nextSortOrder={Math.max(0, ...items.map((i) => i.sortOrder ?? 0)) + 1}
        existingItemCodes={items.map((i) => i.itemCode).filter((code): code is string => !!code)}
        existingCategories={categories}
        onSaved={setSavedMessage}
      />

      <DeactivatePriceListModal
        opened={deactivateModalOpened}
        priceListName={priceList.name ?? ''}
        onClose={() => setDeactivateModalOpened(false)}
        onConfirm={() => id && deactivatePriceListMutation.mutate({ id })}
        loading={deactivatePriceListMutation.isPending}
        isPaused={deactivatePriceListMutation.isPaused}
      />

      <DeactivatePriceListItemModal
        opened={!!deactivateItem}
        itemCode={deactivateItem?.itemCode ?? ''}
        onClose={() => setDeactivateItem(null)}
        onConfirm={() =>
          id &&
          deactivateItem?.id &&
          deactivateItemMutation.mutate({ id, itemId: deactivateItem.id })
        }
        loading={deactivateItemMutation.isPending}
        isPaused={deactivateItemMutation.isPaused}
      />
    </Container>
  );
};
